/**
 * PR34: the ONE canonical Mobile/Desktop classification, shared by the
 * browser and the Netlify Functions layer (imported there by relative path,
 * so this module must stay dependency-free - no `@/` imports, no `window`).
 *
 * `Platform` is what a run is played on and what a leaderboard shows:
 *  - `"mobile"`  - a phone-class viewport, exactly the game's existing
 *    `isPhoneLikeViewport()` rule (touch/coarse-pointer capable AND a
 *    shorter side <= `PHONE_MAX_SHORT_SIDE_PX`);
 *  - `"desktop"` - everything else, including tablets and touch laptops.
 * It is decided once at run start (see `detectPlatform()`) and frozen for
 * the whole run - never re-derived from which input was actually used.
 *
 * `StoredPlatform` adds `"unknown"`, which only ever appears on database
 * rows written before PR34 (or by a pre-PR34 client/queued payload that
 * never carried a platform). It is never a category a client plays in or
 * asks to see.
 */
export type Platform = "mobile" | "desktop";
export type StoredPlatform = Platform | "unknown";

export const PLATFORMS: readonly Platform[] = ["mobile", "desktop"];

export function isPlatform(value: unknown): value is Platform {
  return value === "mobile" || value === "desktop";
}

/** The short label shown on a platform-specific leaderboard. */
export function platformLabel(platform: Platform): string {
  return platform === "mobile" ? "MOBILE" : "DESKTOP";
}

/**
 * The Mobile-only Campaign gate rule, in its purest form - the single
 * source for both the client (`getEffectiveTargetLength()`) and the server
 * (`validateRunSubmission()`; mirrored in SQL by
 * `public.effective_target_length()`):
 *
 *   Mobile + Mars/Saturn/Sun  ->  Math.round(rawTarget * 0.85)
 *   anything else             ->  rawTarget (unchanged)
 *
 * Only the snake length required to open the normal length-based gate
 * changes - nothing about scoring, stars, speed, growth or the map.
 */
export const MOBILE_GATE_WORLD_IDS: readonly string[] = ["w3", "w4", "w5"];
export const MOBILE_GATE_TARGET_FACTOR = 0.85;

export function effectiveTargetLengthFor(worldId: string, rawTargetLength: number, platform: StoredPlatform | undefined): number {
  if (platform !== "mobile" || !MOBILE_GATE_WORLD_IDS.includes(worldId)) return rawTargetLength;
  return Math.max(1, Math.round(rawTargetLength * MOBILE_GATE_TARGET_FACTOR));
}
